import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import PageTitle from 'components/PageTitle';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { LeagueName } from '../../consts';
import ListGroup from 'react-bootstrap/ListGroup';
import { NavLink } from 'react-router-dom';
import { useGetLeaguesQuery, useLazyGetLeagueMatchesQuery } from 'app/api';
import Loader from 'components/Loader/Loader.style';
import { BLACK } from 'styles/colors';
import { AgeCategory } from '../Live/Live.style';
import { ILeague } from 'types/types';
import { LoaderWrapper } from './Matches.style';

const MetaTitle = `Mecze - ${LeagueName}`;
const MetaDescription = 'Harmonogram meczów ligi wspinaczkowej';

const Matches: React.FC = () => {
  const { data: leagues, isLoading: leaguesLoading } = useGetLeaguesQuery();
  const [fetchMatches] = useLazyGetLeagueMatchesQuery();
  const [leaguesWithMatches, setLeaguesWithMatches] = useState<ILeague[]>([]);

  useEffect(() => {
    if (!leagues) return;

    setLeaguesWithMatches(
      leagues.map((league) => ({
        ...league,
        matches: [],
        isLoading: true,
      })),
    );

    leagues.forEach(async (league) => {
      try {
        const matches = await fetchMatches({ league_id: league.id }).unwrap();
        setLeaguesWithMatches((prev) =>
          prev.map((l) =>
            l.id === league.id ? { ...l, matches, isLoading: false } : l,
          ),
        );
      } catch (error) {
        setLeaguesWithMatches((prev) =>
          prev.map((l) =>
            l.id === league.id ? { ...l, matches: [], isLoading: false } : l,
          ),
        );
      }
    });
  }, [leagues, fetchMatches]);

  return (
    <>
      <Helmet>
        <title>{MetaTitle}</title>
        <meta name="description" content={MetaDescription} />
        <meta property="og:title" content={MetaTitle} />
        <meta property="og:description" content={MetaDescription} />
        <meta name="twitter:title" content={MetaTitle} />
        <meta name="twitter:description" content={MetaDescription} />
      </Helmet>
      <section>
        <PageTitle>Mecze</PageTitle>
        <Container className="pt-5">
          {leaguesLoading && (
            <LoaderWrapper className="d-flex justify-content-center align-items-center">
              <Loader color={BLACK} size={10} />
            </LoaderWrapper>
          )}
          <Row className="justify-content-center row-gap-2rem">
            {leaguesWithMatches.map((league) => (
              <Col xs={12} lg={5} key={league.id}>
                <h2 className="text-center d-flex flex-row justify-content-center align-items-center column-gap-2">
                  <AgeCategory>{league.attrs.ageCategory}</AgeCategory>
                  {league.attrs.ageCategoryDescription}
                </h2>
                {league.isLoading && (
                  <LoaderWrapper className="d-flex justify-content-center align-items-center">
                    <Loader color={BLACK} size={10} />
                  </LoaderWrapper>
                )}
                {!league.isLoading &&
                  league.matches &&
                  league.matches.length > 0 && (
                    <ListGroup className="mb-3">
                      {league.matches.map((match) => {
                        const scoreVisible =
                          (match.teams[0].team.points ?? 0) > 0 ||
                          (match.teams[1].team.points ?? 0) > 0;

                        return (
                          <ListGroup.Item
                            key={match.id}
                            action
                            as={NavLink}
                            to={`/${league.id}/matches/${match.id}/`}
                            className="text-center"
                          >
                            {`${new Date(
                              (match.start_date || 0) * 1000,
                            ).toLocaleDateString('pl-PL', {
                              day: 'numeric',
                              month: 'long',
                            })}, ${new Date(
                              (match.start_date || 0) * 1000,
                            ).toLocaleTimeString('pl-PL', {
                              hour: 'numeric',
                              minute: 'numeric',
                            })}`}

                            <div className="d-flex flex-row justify-content-center align-items-center mt-2 column-gap-2">
                              <span className="fw-bold">
                                {match.teams[0].team.name}
                              </span>

                              <span className="d-inline-block">
                                <span
                                  className={`d-inline-block border rounded p-2 ${
                                    match.teams[0].team.points! >
                                    match.teams[1].team.points!
                                      ? 'bg-success border-success text-white'
                                      : 'bg-body'
                                  } ${!scoreVisible ? 'in' : ''}visible`}
                                >
                                  {match.teams[0].team.points}
                                </span>{' '}
                                :{' '}
                                <span
                                  className={`d-inline-block border rounded p-2 ${
                                    match.teams[1].team.points! >
                                    match.teams[0].team.points!
                                      ? 'bg-success border-success text-white'
                                      : 'bg-body'
                                  } ${!scoreVisible ? 'in' : ''}visible`}
                                >
                                  {match.teams[1].team.points}
                                </span>
                              </span>

                              <span className="fw-bold">
                                {match.teams[1].team.name}
                              </span>
                            </div>
                          </ListGroup.Item>
                        );
                      })}
                    </ListGroup>
                  )}
              </Col>
            ))}
          </Row>
        </Container>
      </section>
    </>
  );
};

export default Matches;
